// src/lib/cms.js
// ──────────────────────────────────────────────────────────────────────────
// Content & data helpers built on Supabase. Used by the public site (to read
// editable content) and by the Admin area (to read + write it).
//
// Tables: site_content (key/value), news, applications.  Bucket: media (public).
// All functions degrade gracefully if Supabase isn't configured.
// ──────────────────────────────────────────────────────────────────────────

import { supabase, supabaseReady } from './supabase';

// ── Editable site content (key → JSON value) ────────────────
export async function fetchSiteContent() {
  if (!supabaseReady) return {};
  const { data, error } = await supabase.from('site_content').select('key, value');
  if (error || !data) return {};
  const out = {};
  data.forEach((r) => { out[r.key] = r.value; });
  return out;
}

export async function saveSiteContent(key, value) {
  if (!supabaseReady) throw new Error('Supabase not configured');
  const { error } = await supabase.from('site_content')
    .upsert({ key, value, updated_at: new Date().toISOString() });
  if (error) throw error;
}

// ── Image / file uploads → public "media" bucket ────────────
export async function uploadMedia(file) {
  if (!supabaseReady) throw new Error('Supabase not configured');
  const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
  const path = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const { error } = await supabase.storage.from('media').upload(path, file, { upsert: false });
  if (error) throw error;
  const { data } = supabase.storage.from('media').getPublicUrl(path);
  return data.publicUrl;
}

// ── News ────────────────────────────────────────────────────
export async function fetchNews() {
  if (!supabaseReady) return null; // null = "use built-in seed"
  const { data, error } = await supabase.from('news').select('*').order('created_at', { ascending: false });
  if (error) return null;
  return data;
}
export async function saveNews(row) {
  if (!supabaseReady) throw new Error('Supabase not configured');
  const { data, error } = await supabase.from('news').upsert(row).select();
  if (error) throw error;
  return data;
}
export async function removeNews(id) {
  if (!supabaseReady) throw new Error('Supabase not configured');
  const { error } = await supabase.from('news').delete().eq('id', id);
  if (error) throw error;
}

// ── Applications (admin) ────────────────────────────────────
export const APPLICATION_STATUSES = ['pending', 'approved', 'rejected', 'enrolled'];

export async function fetchApplications() {
  if (!supabaseReady) throw new Error('Supabase not configured');
  const { data, error } = await supabase.from('applications').select('*').order('created_at', { ascending: false });
  if (error) throw error;
  // Rows created before the review migration have no status — treat as pending.
  return (data || []).map((r) => ({ ...r, status: r.status || 'pending' }));
}

// Move an application through the review cycle. Records who decided and when.
export async function setApplicationStatus(id, status, note) {
  if (!supabaseReady) throw new Error('Supabase not configured');
  if (!APPLICATION_STATUSES.includes(status)) throw new Error(`Unknown status "${status}"`);
  const { data: u } = await supabase.auth.getUser();
  const patch = {
    status,
    reviewed_at: new Date().toISOString(),
    reviewed_by: u?.user?.email || null,
  };
  if (note !== undefined) patch.review_note = note || null;
  const { data, error } = await supabase.from('applications').update(patch).eq('id', id).select();
  if (error) throw error;
  return data?.[0];
}

// Suggest the next free Student ID for the current year, e.g. MTI/2026/007.
export async function suggestStudentId(prefix = 'MTI') {
  const year = new Date().getFullYear();
  const fallback = `${prefix}/${year}/001`;
  if (!supabaseReady) return fallback;
  const { data, error } = await supabase.from('profiles')
    .select('student_id').not('student_id', 'is', null).like('student_id', `${prefix}/${year}/%`);
  if (error || !data?.length) return fallback;
  const highest = data.reduce((max, r) => {
    const n = parseInt(String(r.student_id).split('/').pop(), 10);
    return Number.isFinite(n) && n > max ? n : max;
  }, 0);
  return `${prefix}/${year}/${String(highest + 1).padStart(3, '0')}`;
}

// Send an approved applicant their welcome email (Student ID + a one-time
// link to choose a password). Delivered to the real address they applied with,
// not the synthetic login address. Needs the "notify-applicant" function.
export async function sendApplicantWelcome(applicationId, studentId) {
  if (!supabaseReady) throw new Error('Supabase not configured');
  const { data: session } = await supabase.auth.getSession();
  const token = session?.session?.access_token;
  const base = process.env.REACT_APP_SUPABASE_URL;
  const res = await fetch(`${base}/functions/v1/notify-applicant`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ application_id: applicationId, student_id: studentId }),
  });
  const out = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(out.error || 'Could not send the email.');
  return out;
}

// Approve → account. Creates the student, then links & closes the application.
export async function onboardApplicant(application, { studentId, password }) {
  if (!supabaseReady) throw new Error('Supabase not configured');
  const created = await createAccount({
    identifier: studentId,
    fullName: application.name,
    password,
    role: 'student',
  });
  const { error } = await supabase.from('applications')
    .update({ status: 'enrolled', student_profile_id: created.id, reviewed_at: new Date().toISOString() })
    .eq('id', application.id);
  // The account exists either way — a failed link shouldn't look like a failed
  // signup, so surface it as a warning rather than throwing it all away.
  return { ...created, studentId, linkWarning: error?.message || null };
}

// ── Programmes ──────────────────────────────────────────────
export async function fetchPrograms() {
  if (!supabaseReady) return null; // null = "use built-in sample list"
  const { data, error } = await supabase.from('programs').select('*')
    .order('sort', { ascending: true }).order('created_at', { ascending: true });
  if (error) return null;
  return data;
}
export async function saveProgram(row) {
  if (!supabaseReady) throw new Error('Supabase not configured');
  const { data, error } = await supabase.from('programs').upsert(row).select();
  if (error) throw error;
  return data;
}
export async function removeProgram(slug) {
  if (!supabaseReady) throw new Error('Supabase not configured');
  const { error } = await supabase.from('programs').delete().eq('slug', slug);
  if (error) throw error;
}

// ── Library catalogue ───────────────────────────────────────
// Returns null when the table is empty or unreachable, which the Library page
// reads as "show the built-in list" — same convention as programs and news.
// Type and department lists live in src/api/library.js alongside the built-in
// catalogue, so there's one source of truth.
export const LIBRARY_BUCKET = 'library';

export async function fetchLibrary() {
  if (!supabaseReady) return null;
  const { data, error } = await supabase.from('library').select('*')
    .order('sort', { ascending: true }).order('created_at', { ascending: true });
  if (error || !data?.length) return null;
  return data;
}

export async function saveLibraryItem(row) {
  if (!supabaseReady) throw new Error('Supabase not configured');
  const { data, error } = await supabase.from('library').upsert(row).select();
  if (error) throw error;
  return data;
}

export async function removeLibraryItem(slug) {
  if (!supabaseReady) throw new Error('Supabase not configured');
  // Remove the PDF too, so deleting a title doesn't orphan a file nobody can
  // reach (the bucket is private and browsing it means going to the dashboard).
  const { data: row } = await supabase.from('library').select('file_path').eq('slug', slug).maybeSingle();
  if (row?.file_path) await supabase.storage.from(LIBRARY_BUCKET).remove([row.file_path]).catch(() => {});
  const { error } = await supabase.from('library').delete().eq('slug', slug);
  if (error) throw error;
}

// Upload a PDF into the PRIVATE library bucket. Returns the object name to
// store as file_path — not a URL, because the bucket only ever hands out
// short-lived signed URLs.
export async function uploadLibraryFile(file, slug) {
  if (!supabaseReady) throw new Error('Supabase not configured');
  const ext = (file.name.split('.').pop() || 'pdf').toLowerCase();
  const path = `${slug || Date.now()}.${ext}`;
  const { error } = await supabase.storage.from(LIBRARY_BUCKET)
    .upload(path, file, { upsert: true, contentType: file.type || 'application/pdf' });
  if (error) throw error;
  return path;
}

// Short-lived signed URL for reading one item (10 minutes).
export async function signLibraryFile(filePath) {
  if (!supabaseReady || !filePath) return '';
  const { data, error } = await supabase.storage.from(LIBRARY_BUCKET).createSignedUrl(filePath, 600);
  return error ? '' : (data?.signedUrl || '');
}

// ── Portal: notices, documents, results ─────────────────────
export async function fetchNotices() {
  if (!supabaseReady) return [];
  const { data, error } = await supabase.from('notices').select('*').order('created_at', { ascending: false });
  return error ? [] : data;
}
export async function saveNotice(row) {
  const { data, error } = await supabase.from('notices').upsert(row).select();
  if (error) throw error;
  if (!row.id) {
    notify({
      title: row.title,
      body: row.body,
      kind: 'notice',
      audience: row.audience === 'staff' ? 'staff' : row.audience === 'student' ? 'students' : 'all',
    });
  }
  return data;
}
export async function removeNotice(id) {
  const { error } = await supabase.from('notices').delete().eq('id', id);
  if (error) throw error;
}

export async function fetchDocuments() {
  if (!supabaseReady) return [];
  const { data, error } = await supabase.from('documents').select('*').order('created_at', { ascending: false });
  return error ? [] : data;
}
export async function saveDocument(row) {
  const { data, error } = await supabase.from('documents').upsert(row).select();
  if (error) throw error; return data;
}
export async function removeDocument(id) {
  const { error } = await supabase.from('documents').delete().eq('id', id);
  if (error) throw error;
}

// Results: students see their own (enforced by RLS); staff/admin see all.
export async function fetchResults() {
  if (!supabaseReady) return [];
  const { data, error } = await supabase.from('results').select('*').order('created_at', { ascending: false });
  return error ? [] : data;
}
export async function saveResult(row) {
  const { data, error } = await supabase.from('results').upsert(row).select();
  if (error) throw error; return data;
}
export async function removeResult(id) {
  const { error } = await supabase.from('results').delete().eq('id', id);
  if (error) throw error;
}

// Upload any file (e.g. portal documents) to the public media bucket.
export const uploadFile = uploadMedia;

// ── Push notifications ──────────────────────────────────────
// Fire-and-forget: a failed notification must never break the action that
// triggered it. A teacher's assignment is saved whether or not push works.
export async function sendNotification(payload) {
  if (!supabaseReady) return null;
  const { data: session } = await supabase.auth.getSession();
  const token = session?.session?.access_token;
  const base = process.env.REACT_APP_SUPABASE_URL;
  const res = await fetch(`${base}/functions/v1/send-push`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  });
  const out = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(out.error || 'Could not send the notification.');
  return out;
}

function notify(payload) {
  sendNotification(payload).catch((e) => console.warn('Notification not sent:', e.message));
}

// ── LMS: classes, enrolments, announcements, assignments ────
export async function fetchClasses() {
  if (!supabaseReady) return [];
  const { data, error } = await supabase.from('classes').select('*').order('created_at', { ascending: false });
  return error ? [] : data;
}
export async function saveClass(row) {
  const { data, error } = await supabase.from('classes').upsert(row).select();
  if (error) throw error; return data;
}
export async function removeClass(id) {
  const { error } = await supabase.from('classes').delete().eq('id', id);
  if (error) throw error;
}

// Enrolments. We resolve a student's email → profile id before enrolling.
export async function fetchEnrolments(classId) {
  if (!supabaseReady) return [];
  let q = supabase.from('enrolments').select('id, class_id, student_id, profiles(email, full_name, student_id)');
  if (classId) q = q.eq('class_id', classId);
  const { data, error } = await q;
  return error ? [] : data;
}
export async function fetchMyEnrolments() {
  if (!supabaseReady) return [];
  const { data: u } = await supabase.auth.getUser();
  if (!u?.user) return [];
  const { data, error } = await supabase.from('enrolments')
    .select('class_id, classes(*)').eq('student_id', u.user.id);
  return error ? [] : (data || []).map((r) => r.classes).filter(Boolean);
}
// Find a person by Student ID *or* email.
// Students provisioned by ID have a synthetic address (MTI/2026/001 →
// mti-2026-001@students.mti.local), so staff can't look them up by a real
// email. Accept whichever the teacher happens to type.
export async function findProfileByIdentifier(identifier) {
  if (!supabaseReady) return null;
  const raw = String(identifier || '').trim();
  if (!raw) return null;
  const { looksLikeEmail, idToEmail } = await import('./studentId');
  const cols = 'id, email, full_name, student_id, role';

  // 1) Exact Student ID match, as typed.
  if (!looksLikeEmail(raw)) {
    const { data } = await supabase.from('profiles').select(cols).eq('student_id', raw).limit(1);
    if (data?.length) return data[0];
  }
  // 2) Email — either a real address or the synthetic one derived from the ID.
  const email = looksLikeEmail(raw) ? raw.toLowerCase() : idToEmail(raw);
  const { data, error } = await supabase.from('profiles').select(cols).eq('email', email).limit(1);
  if (error) throw error;
  return data?.[0] || null;
}

const notFound = (identifier) =>
  new Error(`No account found for "${identifier}". Use their Student ID (e.g. MTI/2026/001) or the email they signed up with.`);

// Resolve an ID/email to the login email stored on the account.
export async function resolveAccountEmail(identifier) {
  const prof = await findProfileByIdentifier(identifier);
  if (!prof) throw notFound(identifier);
  return prof.email;
}

export async function enrolStudent(classId, identifier) {
  const prof = await findProfileByIdentifier(identifier);
  if (!prof) throw notFound(identifier);
  const { error } = await supabase.from('enrolments').insert({ class_id: classId, student_id: prof.id });
  if (error) throw error;
  return prof;
}
// Back-compat: older callers used the email-only name.
export const enrolStudentByEmail = enrolStudent;
export async function unenrol(id) {
  const { error } = await supabase.from('enrolments').delete().eq('id', id);
  if (error) throw error;
}

export async function fetchAnnouncements() {
  if (!supabaseReady) return [];
  const { data, error } = await supabase.from('announcements')
    .select('*, classes(name, code)').order('created_at', { ascending: false });
  return error ? [] : data;
}
export async function saveAnnouncement(row) {
  const { data: u } = await supabase.auth.getUser();
  const payload = { ...row, author_id: row.author_id || u?.user?.id };
  const { data, error } = await supabase.from('announcements').upsert(payload).select();
  if (error) throw error;
  // Only notify on a brand-new post — editing an old one shouldn't re-alert.
  if (!row.id) {
    notify({
      title: row.title,
      body: row.body,
      kind: 'announcement',
      audience: row.audience === 'class' ? 'class' : (row.audience || 'students'),
      class_id: row.class_id || null,
    });
  }
  return data;
}
export async function removeAnnouncement(id) {
  const { error } = await supabase.from('announcements').delete().eq('id', id);
  if (error) throw error;
}

export async function fetchAssignments() {
  if (!supabaseReady) return [];
  const { data, error } = await supabase.from('assignments')
    .select('*, classes(name, code)').order('due_date', { ascending: true });
  return error ? [] : data;
}
export async function saveAssignment(row) {
  const { data: u } = await supabase.auth.getUser();
  const payload = { ...row, author_id: row.author_id || u?.user?.id };
  const { data, error } = await supabase.from('assignments').upsert(payload).select();
  if (error) throw error;
  if (!row.id && row.class_id) {
    notify({
      title: `New assignment: ${row.title}`,
      body: row.due_date ? `Due ${new Date(row.due_date).toLocaleDateString()}` : 'No due date set.',
      kind: 'assignment',
      audience: 'class',
      class_id: row.class_id,
    });
  }
  return data;
}
export async function removeAssignment(id) {
  const { error } = await supabase.from('assignments').delete().eq('id', id);
  if (error) throw error;
}

// ── Admin: provision accounts via the secure Edge Function ──────────────────
// Requires the "create-student" function deployed. Adds role + must-change-pw.
// `identifier` may be a Student ID (students) or a real email (teachers/staff).
export async function createAccount({ identifier, fullName, password, role = 'student' }) {
  if (!supabaseReady) throw new Error('Supabase not configured');
  const { looksLikeEmail, idToEmail } = await import('./studentId');
  const raw = String(identifier || '').trim();
  if (!raw) throw new Error('A Student ID or email is required.');
  const isEmail = looksLikeEmail(raw);
  const email = isEmail ? raw.toLowerCase() : idToEmail(raw);

  const { data: session } = await supabase.auth.getSession();
  const token = session?.session?.access_token;
  const base = process.env.REACT_APP_SUPABASE_URL;
  const res = await fetch(`${base}/functions/v1/create-student`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ email, password, full_name: fullName, student_id: isEmail ? null : raw, role }),
  });
  const out = await res.json();
  if (!res.ok) throw new Error(out.error || 'Could not create account');
  return { ...out, email };
}

export const createStudentAccount = ({ studentId, fullName, password, role = 'student' }) =>
  createAccount({ identifier: studentId, fullName, password, role });

export const createStaffAccount = ({ email, fullName, password, role = 'staff' }) =>
  createAccount({ identifier: email, fullName, password, role });

// List students (admin/staff). Reads profiles.
export async function fetchStudents() {
  if (!supabaseReady) return [];
  const { data, error } = await supabase.from('profiles').select('*').eq('role', 'student').order('created_at', { ascending: false });
  return error ? [] : data;
}
// List teachers & staff — so a promoted account doesn't vanish from the admin UI.
export async function fetchStaff() {
  if (!supabaseReady) return [];
  const { data, error } = await supabase.from('profiles').select('*')
    .in('role', ['staff', 'admin']).order('created_at', { ascending: false });
  return error ? [] : data;
}
// ── Admin allowlist ─────────────────────────────────────────
// The admins table is an email allowlist checked against the signed-in user's
// JWT email. Adding an address that has no account yet is fine — it takes
// effect the moment they sign in with it.
export async function fetchAdmins() {
  if (!supabaseReady) return [];
  const { data, error } = await supabase.from('admins').select('*').order('added_at', { ascending: true });
  return error ? [] : data;
}

// role: 'owner' (full access) or 'content' (everything but account management)
export async function addAdmin(email, role = 'content') {
  if (!supabaseReady) throw new Error('Supabase not configured');
  const clean = String(email || '').trim().toLowerCase();
  if (!/\S+@\S+\.\S+/.test(clean)) throw new Error('Enter a valid email address.');
  if (!['owner', 'content'].includes(role)) throw new Error('Unknown admin type.');
  const { data: u } = await supabase.auth.getUser();
  const { error } = await supabase.from('admins')
    .upsert({ email: clean, role, added_by: u?.user?.email || null }, { onConflict: 'email' });
  if (error) throw error;
  return clean;
}

export async function setAdminRole(email, role) {
  if (!['owner', 'content'].includes(role)) throw new Error('Unknown admin type.');
  const { error } = await supabase.from('admins')
    .update({ role }).eq('email', String(email).trim().toLowerCase());
  if (error) throw error;
}

// The database rejects removing yourself or the last admin; surface those
// messages as-is since they already explain what to do instead.
export async function removeAdmin(email) {
  if (!supabaseReady) throw new Error('Supabase not configured');
  const { error } = await supabase.from('admins').delete().eq('email', String(email).trim().toLowerCase());
  if (error) throw new Error(error.message || 'Could not remove that admin.');
}

export async function setProfileRole(id, role) {
  const { error } = await supabase.from('profiles').update({ role }).eq('id', id);
  if (error) throw error;
}

// ── Submissions & grading (Phase 5) ─────────────────────────
export async function fetchMySubmissions() {
  if (!supabaseReady) return [];
  const { data: u } = await supabase.auth.getUser();
  if (!u?.user) return [];
  const { data, error } = await supabase.from('submissions').select('*').eq('student_id', u.user.id);
  return error ? [] : data;
}
export async function submitWork({ assignmentId, note, fileUrl }) {
  const { data: u } = await supabase.auth.getUser();
  if (!u?.user) throw new Error('Not signed in');
  const row = {
    assignment_id: assignmentId, student_id: u.user.id, student_email: u.user.email,
    note: note || null, file_url: fileUrl || null,
  };
  // upsert on (assignment_id, student_id) so a student can update before grading
  const { error } = await supabase.from('submissions')
    .upsert(row, { onConflict: 'assignment_id,student_id' });
  if (error) throw error;
}
export async function fetchSubmissionsForAssignment(assignmentId) {
  if (!supabaseReady) return [];
  const { data, error } = await supabase.from('submissions')
    .select('*').eq('assignment_id', assignmentId).order('created_at', { ascending: true });
  return error ? [] : data;
}
export async function gradeSubmission(id, grade, feedback) {
  const { data, error } = await supabase.from('submissions')
    .update({ grade, feedback, graded_at: new Date().toISOString() }).eq('id', id)
    .select('student_id');
  if (error) throw error;
  const studentId = data?.[0]?.student_id;
  // Straight to the one student — never broadcast somebody's grade.
  if (studentId) {
    notify({
      title: 'Your work has been graded',
      body: `Grade: ${grade}${feedback ? ` — ${feedback}` : ''}`,
      kind: 'grade',
      user_ids: [studentId],
    });
  }
}
